import { Listbox, Menu, Transition } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { FunnelIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import React, {
	Fragment,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import SettingsContext from '../../context/SettingsContext';

import useModal from '../../hooks/useModal';

import EllipsisDropdown, { EllipsisDropdownItem } from '../EllipsisDropdown';
import { classNames } from '../helpers/functions';
import { Button } from '../Button';
import LoadingSkeleton from '../Shell/LoadingSkeleton';

const people = [
	{ id: 1, name: 'Wade Cooper', online: true },
	{ id: 2, name: 'Arlene Mccoy', online: false },
	{ id: 3, name: 'Devon Webb', online: false },
	{ id: 4, name: 'Tom Cook', online: true },
	{ id: 5, name: 'Tanya Fox', online: false },
	{ id: 6, name: 'Hellen Schmidt', online: true },
	{ id: 7, name: 'Caroline Schultz', online: true },
	{ id: 8, name: 'Mason Heaney', online: false },
	{ id: 9, name: 'Claudie Smitham', online: true },
	{ id: 10, name: 'Emil Schaefer', online: false },
];

const Column = ({
	children = '',
	size = 'md',
	hiddenOnMobile,
	key,
	className,
	...props
}) => {
	const SIZING = {
		sm: 'relative px-7 sm:w-12 sm:px-6',
		md: '',
		lg: 'min-w-[12rem] ',
	};

	return (
		<th
			key={key}
			scope="col"
			className={classNames(
				hiddenOnMobile ? 'hidden sm:table-cell' : '',
				'px-3 py-3.5 text-left text-sm font-semibold text-gray-900',

				SIZING[size]
			)}
			{...props}
		>
			{children}
		</th>
	);
};

const Cell = ({
	children = '',
	key,
	size = 'md',
	hiddenOnMobile,
	className,
	...props
}) => {
	const SIZING = {
		sm: 'relative px-7 sm:w-12 sm:px-6',
		md: '',
		lg: 'min-w-[12rem] ',
	};
	return (
		<td
			key={key}
			className={classNames(
				hiddenOnMobile ? 'hidden sm:table-cell' : '',
				'whitespace-nowrap px-3 py-4 text-xs text-gray-500 relative',
				className
			)}
			{...props}
		>
			{children}
		</td>
	);
};

const Table = ({
	columns = [],
	dataSource = [],
	actions = [],
	onRow,
	onEmpty,
	grouped = false,
	emptyTableText,

	hasCheckboxes = false,
	ActionButton,

	exportable = false,
	onExport,

	extendableColumns = false,
	extendedColumns = [],
	hideExtendableColumns = false,

	filterable = false,
	pagination,

	className,
	trClassName,

	tableActions = [],
}) => {
	const checkbox = useRef();
	const [checked, setChecked] = useState(false);
	const [indeterminate, setIndeterminate] = useState(false);
	const [selectedAssets, setSelectedAssets] = useState([]);

	const {
		open: isDeleteModalOpen,
		setOpen: onOpenDeleteModal,
		setClose: onCloseDeleteModal,
	} = useModal('deleteModal');

	const {
		settings,
		loading: isLoadingSettings,
		refresh: refreshSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const shownColumns = [
		...columns,
		...extendedColumns
			.filter((ec) => settings?.user?.inventoryColumns?.includes(ec.key))
			.map((column) => ({
				title: column?.label,
				key: column?.key,
				dataIndex: column?.key,
				hideOnMobile: hideExtendableColumns,
			})),
	];

	useLayoutEffect(() => {
		if (hasCheckboxes) {
			const isIndeterminate =
				selectedAssets.length > 0 && selectedAssets.length < dataSource?.length;
			setChecked(
				selectedAssets.length === dataSource?.length && dataSource?.length !== 0
			);
			setIndeterminate(isIndeterminate);
			if (checkbox?.current) {
				checkbox.current.indeterminate = isIndeterminate;
			}
		}
	}, [selectedAssets]);

	function toggleAll() {
		setSelectedAssets(
			checked || indeterminate ? [] : dataSource?.map((row) => row.id)
		);
		setChecked(!checked && !indeterminate);
		setIndeterminate(false);
	}

	const Row = (row, rowIndex) => {
		return (
			<tr
				key={`row-${rowIndex}`}
				className={classNames(
					onRow ? 'cursor-pointer' : '',
					'overflow-auto',
					typeof trClassName === 'function' ? trClassName(row, rowIndex) : ''
				)}
				onClick={() =>
					typeof onRow === 'function' ? onRow(row, rowIndex) : null
				}
			>
				{hasCheckboxes && (
					<Cell
						key={'checkboxes'}
						hiddenOnMobile
						size="sm"
						onClick={(e) => e.stopPropagation()}
					>
						{selectedAssets.includes(row?.id) && (
							<div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
						)}
						<input
							type="checkbox"
							className="absolute left-4 top-1/2 -mt-2 h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
							value={row.id}
							checked={selectedAssets.includes(row.id)}
							onChange={(e) =>
								setSelectedAssets(
									e.target.checked
										? [...selectedAssets, row.id]
										: selectedAssets.filter((p) => p !== row.id)
								)
							}
						/>
					</Cell>
				)}

				{shownColumns?.map((col, colIndex) => (
					<Cell
						key={`cell-${colIndex}`}
						hiddenOnMobile={col.hiddenOnMobile}
						className={col.className}
					>
						{col.render
							? col.render(row[col.dataIndex], row, colIndex)
							: row[col.dataIndex] ||
							  row?.attributes?.find((o) => o.key === col.dataIndex)?.value}
					</Cell>
				))}

				{actions?.filter((a) =>
					typeof a?.hidden === 'function' ? !a?.hidden(row) : true
				).length ? (
					<Cell className="text-right" onClick={(e) => e.stopPropagation()}>
						<EllipsisDropdown>
							{actions?.map((action) => {
								let hidden = false;
								if (action?.hidden && typeof action?.hidden === 'function') {
									hidden = action?.hidden(row);
								}

								return (
									!hidden && (
										<EllipsisDropdownItem
											onClick={() => {
												const onAction = () =>
													typeof action?.onClick
														? action?.onClick(row)
														: () => {};
												if (action?.danger) {
													onOpenDeleteModal({
														onConfirm: () => {
															onAction();
															onCloseDeleteModal();
														},
														confirmText: action?.confirmText || 'Delete',
														title: action.danger,
													});
												} else {
													onAction();
												}
											}}
											danger={action.danger}
										>
											{action.name}
										</EllipsisDropdownItem>
									)
								);
							})}
						</EllipsisDropdown>
					</Cell>
				) : null}
			</tr>
		);
	};

	let showEmptyMessage = false;

	const isDataSourceArray = Array.isArray(dataSource);

	if (isDataSourceArray) {
		if (!dataSource?.length) {
			showEmptyMessage = true;
		}
	} else {
		if (dataSource && !Object.keys(dataSource)?.length) {
			showEmptyMessage = true;
		}
	}

	return (
		<>
			{showEmptyMessage && !!emptyTableText ? (
				<p className="text-center py-12 text-gray-600">{emptyTableText}</p>
			) : (
				<>
					<div className="flex justify-between items-center mb-4">
						<div>
							{filterable && (
								<Button type="secondary" Icon={FunnelIcon}>
									Filters
								</Button>
							)}
						</div>

						<div className="flex items-center">
							{tableActions?.length
								? tableActions.map((tableAction) => (
										<Button
											type={tableAction?.type || 'secondary'}
											onClick={() => {
												if (typeof tableAction?.onClick === 'function') {
													tableAction.onClick();
												}
											}}
										>
											{tableAction?.label}
										</Button>
								  ))
								: null}

							{extendableColumns && (
								<Menu as="div" className="relative inline-block text-left">
									{({ open }) => (
										<>
											<div>
												<Menu.Button
													onClick={() => refreshSettings()}
													className="ml-0 sm:ml-3 inline-flex items-center rounded-md border px-4 py-4 sm:py-2 text-xs font-medium border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
												>
													<ViewColumnsIcon className="w-4 h-4 mr-1.5 text-gray-700" />
													Columns
												</Menu.Button>
											</div>
											{open ? (
												<Menu.Items
													static
													className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20"
												>
													<div className="p-3 divide-y divide-gray-100">
														{!isLoadingSettings ? (
															extendedColumns?.map((col) => {
																return (
																	<Menu.Item
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																		}}
																	>
																		<div className="cursor-pointer flex items-center py-1.5">
																			<input
																				type="checkbox"
																				className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
																				value={col.key}
																				checked={settings?.user?.inventoryColumns?.includes(
																					col.key
																				)}
																				onChange={(e) => {
																					updateSettings(
																						{
																							type: 'user',
																							key: 'inventoryColumns',
																							value: e.target.checked
																								? [
																										...(settings?.user
																											?.inventoryColumns || []),
																										col.key,
																								  ]
																								: settings?.user?.inventoryColumns?.filter(
																										(c) => c !== col.key
																								  ),
																						},
																						() =>
																							refreshSettings(null, null, {
																								silent: true,
																							})
																					);
																				}}
																			/>
																			<div className="text-xxs text-gray-700">
																				{col?.label}
																			</div>
																		</div>
																	</Menu.Item>
																);
															})
														) : (
															<LoadingSkeleton />
														)}
													</div>
												</Menu.Items>
											) : null}
										</>
									)}
								</Menu>
							)}

							{exportable && (
								<Button
									type={selectedAssets.length ? 'primary' : 'secondary'}
									onClick={() => {
										if (typeof onExport === 'function') {
											onExport(selectedAssets);
											setSelectedAssets([]);
											setIndeterminate(false);
											setChecked(false);
										}
									}}
								>
									{selectedAssets.length
										? `Export ${selectedAssets.length}`
										: 'Export All'}
								</Button>
							)}

							{hasCheckboxes && ActionButton ? ActionButton : null}
						</div>
					</div>

					<table
						className={classNames(
							'min-w-full table-fixed divide-y divide-gray-300',
							!pagination && className
						)}
					>
						<thead>
							<tr>
								{hasCheckboxes && (
									<Column size="sm">
										<input
											type="checkbox"
											className="absolute left-4 top-1/2 -mt-2 h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
											ref={checkbox}
											checked={checked}
											onChange={toggleAll}
										/>
									</Column>
								)}
								{shownColumns.map((col) => (
									<Column hiddenOnMobile={col.hiddenOnMobile} size={col.size}>
										{col.title}
									</Column>
								))}
								{actions?.length ? <Column size="sm" /> : null}
							</tr>
						</thead>
						<tbody className="">
							{grouped
								? Object.keys(dataSource).map((key) => (
										<Fragment key={key}>
											<tr
												className={
													selectedAssets.includes(key)
														? 'bg-gray-50'
														: undefined
												}
											>
												<th
													colSpan={shownColumns.length + actions.length}
													scope="colgroup"
													className="bg-gray-50 px-4 py-2 text-left text-xs font-semibold text-gray-600 sm:px-6"
												>
													{key}
												</th>
											</tr>
											{dataSource[key].map(Row)}
										</Fragment>
								  ))
								: dataSource?.map(Row)}
						</tbody>
					</table>

					{pagination && (
						<div className={classNames('mt-4 flex justify-end', className)}>
							<div className="flex items-center">
								<div className="mr-2 text-xs text-gray-500">
									Viewing <span className="font-semibold">{`1-10`}</span> of{' '}
									<span className="font-semibold">54</span>
								</div>
								<div className="flex items-center">
									<ChevronLeftIcon className="w-6 h-6 text-gray-500" />
									<ChevronRightIcon className="w-6 h-6 text-gray-500" />
								</div>
							</div>
						</div>
					)}

					{dataSource?.length === 0 && !!onEmpty && <div></div>}
				</>
			)}
		</>
	);

	return (
		<>
			<div className="-mx-4 mb-8 shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
				<table className="min-w-full divide-y divide-gray-300">
					<thead className="bg-gray-50">
						<tr>
							{hasCheckboxes && <Column />}
							{columns.map((col) => (
								<Column hiddenOnMobile={col.hiddenOnMobile}>{col.title}</Column>
							))}
							{actions?.length ? <Column /> : null}
						</tr>
					</thead>
					<tbody className="bg-white">
						{grouped
							? Object.keys(dataSource).map((key) => (
									<Fragment key={key}>
										<tr className="border-t border-gray-200">
											<th
												colSpan={columns.length + actions.length}
												scope="colgroup"
												className="bg-gray-50 px-4 py-2 text-left text-xs font-semibold text-gray-600 sm:px-6"
											>
												{key}
											</th>
										</tr>
										{dataSource[key].map(Row)}
									</Fragment>
							  ))
							: dataSource?.map(Row)}
					</tbody>
				</table>
			</div>
			{showEmptyMessage && !!emptyTableText && (
				<p className="text-center py-12 text-gray-600">{emptyTableText}</p>
			)}
		</>
	);
};

export default Table;
